import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";

const SERVER_URL = window.location.protocol + "//" + window.location.host;

const modalStyle = {
   position: "absolute",
   top: "50%",
   left: "50%",
   transform: "translate(-50%, -50%)",
   width: "90%",
   maxWidth: 600,
   bgcolor: "background.paper",
   border: "2px solid #000",
   boxShadow: 24,
   p: 4,
   display: "flex",
   flexDirection: "column",
   alignItems: "center",
};

export default function NonLegalCardsDataGrid({ data, height, onRemove }) {
   const [previewCard, setPreviewCard] = useState(null);
   const [previewOpen, setPreviewOpen] = useState(false);

   const handlePreview = (card) => {
      setPreviewCard(card);
      setPreviewOpen(true);
   };

   const handleClosePreview = () => {
      setPreviewOpen(false);
      setPreviewCard(null);
   };

   const columns = [
      {
         field: "id",
         headerName: "ID",
         editable: false,
         width: 80,
         filterable: false,
      },
      {
         field: "image",
         headerName: "Image",
         editable: false,
         width: 120,
         filterable: false,
         renderCell: (params) => {
            const fileName = `${(params.row.set || '').toUpperCase()}_${params.row.name}`;
            return (
               <Box
                  sx={{
                     height: "100%",
                     p: 1,
                     backgroundColor: "#fff",
                  }}
               >
                  <img
                     src={
                        SERVER_URL +
                        "/cardimages/thumbs/" +
                        encodeURIComponent(fileName) +
                        ".jpg"
                     }
                     height='100%'
                     alt={params.row.name}
                     onError={(e) => {
                        e.target.src = SERVER_URL + "/images/nocard.png";
                     }}
                  />
               </Box>
            );
         },
      },
      {
         field: "name",
         headerName: "Name",
         editable: false,
         width: 300,
      },
      {
         field: "set",
         headerName: "Set",
         editable: false,
         width: 150,
      },
      {
         field: "preview",
         headerName: "Preview",
         editable: false,
         width: 120,
         filterable: false,
         sortable: false,
         renderCell: (params) => (
            <Button
               variant="outlined"
               color="primary"
               onClick={(e) => {
                  e.stopPropagation();
                  handlePreview(params.row);
               }}
            >
               Preview
            </Button>
         ),
      },
      {
         field: "remove",
         headerName: "Remove",
         editable: false,
         width: 150,
         filterable: false,
         sortable: false,
         renderCell: (params) => (
            <Button
               variant="outlined"
               color="error"
               onClick={(e) => {
                  e.stopPropagation();
                  onRemove && onRemove(params.row);
               }}
            >
               REMOVE
            </Button>
         ),
      },
   ];

   const initialState = {
      sorting: {
         sortModel: [{ field: "name", sort: "asc" }],
      },
      columns: {
         columnVisibilityModel: {
            id: false,
         },
      },
   };

   // Data should already have IDs from the database
   const rowsWithIds = data || [];

   // Generate filename with YYYYMMDD format
   const getDateString = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${year}${month}${day}`;
   };

   const getCardFileName = (card) => {
      return `${(card.set || '').toUpperCase()}_${card.name}`;
   };

   return (
      <>
         <Box sx={{ height: height ? height : 400, width: "100%" }}>
            <DataGrid
               getRowId={(row) => row.id || `${row.set}_${row.name}`}
               initialState={initialState}
               sx={{ backgroundColor: "#efefef" }}
               rows={rowsWithIds}
               rowHeight={120}
               columns={columns}
               rowsPerPageOptions={[100, 200, 300, 1000, 5000]}
               components={{ Toolbar: GridToolbar }}
               componentsProps={{
                  toolbar: {
                     csvOptions: {
                        fileName: `NonLegalCards_${getDateString()}`,
                     },
                     printOptions: {
                        disableToolbarButton: true,
                     },
                  },
               }}
               disableSelectionOnClick
            />
         </Box>

         {/* Preview Modal */}
         <Modal open={previewOpen} onClose={handleClosePreview}>
            <Box sx={modalStyle}>
               <Typography
                  variant='h4'
                  component='h4'
                  sx={{ color: "#333", mb: 2, width: "100%" }}
               >
                  Card Preview
                  <Button
                     onClick={handleClosePreview}
                     variant='outlined'
                     sx={{ float: "right" }}
                  >
                     Close
                  </Button>
               </Typography>
               {previewCard && (
                  <Box sx={{ mt: 2 }}>
                     <img
                        src={
                           SERVER_URL +
                           "/cardimages/png/" +
                           encodeURIComponent(getCardFileName(previewCard)) +
                           ".png"
                        }
                        alt={previewCard.name}
                        style={{
                           maxWidth: "100%",
                           height: "auto",
                        }}
                        onError={(e) => {
                           e.target.src = SERVER_URL + "/images/nocard.png";
                        }}
                     />
                     <Typography variant='h6' sx={{ mt: 2 }}>
                        {previewCard.name}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        {previewCard.set}
                     </Typography>
                  </Box>
               )}
            </Box>
         </Modal>
      </>
   );
}
